import style from '@/less/main.less';
import {
  Button,
  Form,
  FormField,
  FormGroup,
  Icon,
  Modal,
  TextArea,
} from '@/semantic-ui/components';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Formik } from 'formik';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-semantic-toasts';
import { object, string } from 'yup';
import Bulletin from '../Bulletin';

interface OnboardingSubmissionDetailsRejectConfirmationModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingSubmissionDetailsRejectConfirmationModal = observer(
  ({ open, onClose }: OnboardingSubmissionDetailsRejectConfirmationModalProps) => {
    const { temporaryEmployeeStore } = useStores();
    const navigation = useNavigate();

    return (
      <Modal
        className={style.modal}
        open={open}
        size="tiny"
        closeIcon={<Icon name="close" size="tiny" color="black" />}
        closeOnDimmerClick={false}
        onClose={onClose}
      >
        <Formik
          initialValues={{ notes: '' }}
          validationSchema={object().shape({
            notes: string().required('Notes are required'),
          })}
          onSubmit={async ({ notes }) => {
            if (temporaryEmployeeStore.current) {
              await temporaryEmployeeStore.reject(temporaryEmployeeStore.current.id, notes);

              onClose();
              toast({
                type: 'success',
                icon: 'check circle',
                title: 'Onboarding Submission Rejected',
                description:
                  'The onboarding submission has been rejected and the notes have been sent to the employee.',
                time: 5000,
              });
              navigation(-1);
            }
          }}
        >
          {({ values, setFieldValue, handleSubmit, handleBlur }) => (
            <>
              <Modal.Header>Reject Onboarding Submission</Modal.Header>

              <Modal.Content>
                <Bulletin
                  errorMessage={temporaryEmployeeStore.errorMessage}
                  isLoading={temporaryEmployeeStore.isLoading}
                  handleDismiss={() => {
                    temporaryEmployeeStore.clearError();
                  }}
                />

                <Modal.Description style={{ marginBottom: 18 }}>
                  Please enter a reason for rejecting this onboarding submission so the employee can
                  resubmit the correct information.
                </Modal.Description>

                <Form>
                  <FormGroup>
                    <FormField width={16}>
                      <label htmlFor="notes">Notes</label>
                      <TextArea
                        id="notes"
                        value={values.notes}
                        placeholder="Notes here..."
                        onChange={(_, data) => setFieldValue('notes', data.value)}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="notes"
                        render={(err) => <span style={{ color: 'red', fontSize: 13 }}>{err}</span>}
                      />
                    </FormField>
                  </FormGroup>
                </Form>
              </Modal.Content>

              <Modal.Actions>
                <Button color="grey" basic style={{ fontSize: '0.9rem' }} onClick={onClose}>
                  Cancel
                </Button>
                <Button color="red" style={{ fontSize: '0.9rem' }} onClick={() => handleSubmit()}>
                  <FontAwesomeIcon icon={['fas', 'user-times']} style={{ marginRight: 8 }} />
                  Reject Submission
                </Button>
              </Modal.Actions>
            </>
          )}
        </Formik>
      </Modal>
    );
  },
);
